import { useParams, Link } from "react-router-dom";
import { videos, siteConfig } from "@/mocks/siteData";
import SEOMeta from "@/components/feature/SEOMeta";
import { ArticleSchema, WebPageSchema } from "@/components/feature/SchemaOrg";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useState } from "react";

const categoryLabelMap: Record<string, string> = {
  dianying: "电影",
  dianshiju: "电视剧",
  zongyi: "综艺",
  dongman: "动漫",
  jilupian: "纪录片",
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [isPlaying, setIsPlaying] = useState(false);
  const video = videos.find((v) => v.id === Number(id));

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50 pt-16">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-20">
          <h1 className="text-2xl font-bold text-foreground-900">内容不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 hover:underline cursor-pointer">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.id !== video.id)
    .filter((v) => v.category === video.category || v.tags.some((t) => video.tags.includes(t)))
    .slice(0, 6);

  const pageTitle = `${video.title} - 91视频在线观看`;
  const pageUrl = `${siteConfig.url}/article/${video.id}`;
  const categoryLabel = categoryLabelMap[video.category] || video.category;

  return (
    <>
      <SEOMeta
        title={pageTitle}
        description={`${video.title}，${video.year}年国产${categoryLabel}，${video.description.slice(0, 100)}。91视频网站免费在线观看，高清流畅。`}
        keywords={`${video.title},91国产${categoryLabel},${video.title}在线观看,${video.year}国产${categoryLabel},${video.tags.join(",")}`}
        canonicalUrl={pageUrl}
        ogType="article"
        ogImage={video.thumbnail}
      />
      <ArticleSchema
        headline={video.title}
        description={video.description}
        url={pageUrl}
        image={video.thumbnail}
        datePublished={`${video.year}-01-01`}
        dateModified={video.updateDate}
      />
      <WebPageSchema
        title={pageTitle}
        description={`${video.title} - ${video.description.slice(0, 100)}`}
        url={pageUrl}
        breadcrumbItems={[
          { name: "首页", item: siteConfig.url },
          { name: categoryLabel, item: `${siteConfig.url}/category/${video.category}` },
          { name: video.title, item: pageUrl },
        ]}
        datePublished={`${video.year}-01-01`}
        dateModified={video.updateDate}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: categoryLabel, href: `/category/${video.category}` },
                { label: video.title },
              ]}
            />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2">
                {/* Video Player */}
                <div className="relative aspect-video rounded-xl overflow-hidden bg-foreground-950">
                  {isPlaying ? (
                    <div className="w-full h-full flex items-center justify-center bg-foreground-950">
                      <div className="text-center text-white">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-500/20 flex items-center justify-center">
                          <i className="ri-play-fill text-3xl text-primary-400 animate-pulse"></i>
                        </div>
                        <p className="text-white/60 text-sm">正在播放中...</p>
                        <button
                          onClick={() => setIsPlaying(false)}
                          className="mt-4 text-sm text-white/80 hover:text-white underline cursor-pointer"
                        >
                          停止播放
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={`${video.title} - 91国产${categoryLabel}在线观看`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-center justify-center">
                        <button
                          onClick={() => setIsPlaying(true)}
                          className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 text-white transition-all hover:scale-110 cursor-pointer shadow-lg"
                          aria-label="播放视频"
                        >
                          <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
                        </button>
                      </div>
                      <div className="absolute bottom-4 right-4 bg-black/70 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {video.duration}
                      </div>
                    </>
                  )}
                </div>

                {/* Video Info */}
                <div className="mt-5">
                  <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                    {video.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <span className="text-sm text-foreground-600">{video.year}</span>
                    <span className="text-foreground-400">·</span>
                    <span className="text-sm text-accent-500 font-semibold flex items-center gap-1">
                      <i className="ri-star-fill text-xs"></i>
                      {video.rating}
                    </span>
                    <span className="text-foreground-400">·</span>
                    <span className="text-sm text-foreground-600">
                      {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}次播放
                    </span>
                    <span className="text-foreground-400">·</span>
                    <span className="text-sm text-foreground-500">更新于 {video.updateDate}</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {video.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                    <span className="text-xs px-2 py-1 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                      {categoryLabel}
                    </span>
                  </div>

                  <div className="mt-6 p-5 bg-background-100 rounded-xl">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-3">影片简介</h3>
                    <p className="text-sm text-foreground-700 leading-relaxed">{video.description}</p>
                  </div>

                  <div className="mt-4 p-5 bg-background-100 rounded-xl">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-3">影片信息</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-foreground-500">导演：</span>
                        <span className="text-foreground-900">{video.director}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">年份：</span>
                        <span className="text-foreground-900">{video.year}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">类型：</span>
                        <span className="text-foreground-900">{categoryLabel}</span>
                      </div>
                      <div>
                        <span className="text-foreground-500">时长：</span>
                        <span className="text-foreground-900">{video.duration}</span>
                      </div>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-foreground-500">主演：</span>
                      <span className="text-foreground-900">{video.cast.join("、")}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar - Related Videos */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-4">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-3">
                    {relatedVideos.map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-28 h-16 sm:w-32 sm:h-18 lg:w-32 lg:h-18 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                          <img
                            src={v.thumbnail}
                            alt={v.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                            {v.title}
                          </h4>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-xs text-accent-500 flex items-center gap-0.5">
                              <i className="ri-star-fill text-[9px]"></i>
                              {v.rating}
                            </span>
                            <span className="text-xs text-foreground-400">·</span>
                            <span className="text-xs text-foreground-500">{v.year}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}